import CachedIcon from '@mui/icons-material/Cached';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import { DataGridPro, GridColDef, GridSortModel } from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getEntityAuditLogs } from '../../store/actions/pages';
import { convertDateToLocal, parseDate } from '../../util/date';
import { dataGridStyles } from '../../common';
import { Loadable } from '../../common';

interface EntityAuditLogsDialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    itemType: string;
    entityId: number;
}

const PAGE_SIZE = 10;
const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'time', sort: 'desc' }];
const toBackendSort = (model: GridSortModel = DEFAULT_SORT_MODEL) => {
    const normalized = model.filter((x) => x.sort === 'asc' || x.sort === 'desc');
    const source = normalized.length > 0 ? normalized : DEFAULT_SORT_MODEL;
    return source.map((x) => ({ id: x.field, direction: x.sort as 'asc' | 'desc' }));
};

function EntityAuditLogsDialog({
    open,
    onClose,
    title,
    itemType,
    entityId,
}: EntityAuditLogsDialogProps) {
    const dispatch = useDispatch();
    const [page, setPage] = useState(0);
    const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);

    const { list = [], page: pageInfo, pending } = useSelector(
        (state: any) => state?.pages?.['entity-audit-logs-dialog'] ?? {}
    );

    const totalCount = pageInfo?.totalRecords ?? 0;

    const loadPage = (nextPage: number) => {
        dispatch(
            getEntityAuditLogs(
                itemType,
                entityId,
                { currentPage: nextPage + 1, recordsPerPage: PAGE_SIZE },
                toBackendSort(sortModel),
                null
            )
        );
    };

    useEffect(() => {
        if (open && entityId) {
            setPage(0);
            setSortModel(DEFAULT_SORT_MODEL);
            dispatch(
                getEntityAuditLogs(
                    itemType,
                    entityId,
                    { currentPage: 1, recordsPerPage: PAGE_SIZE },
                    toBackendSort(DEFAULT_SORT_MODEL),
                    null
                )
            );
        }
    }, [open, entityId, itemType, dispatch]);

    const handlePageChange = (model: { page: number; pageSize: number }) => {
        if (model.page !== page) {
            setPage(model.page);
            loadPage(model.page);
        }
    };

    const handleRefresh = () => {
        loadPage(page);
    };

    const handleSortModelChange = (nextSortModel: GridSortModel) => {
        const normalizedSortModel = nextSortModel.filter((x) => x.sort === 'asc' || x.sort === 'desc');
        const safeSortModel = normalizedSortModel.length > 0 ? normalizedSortModel : DEFAULT_SORT_MODEL;
        setSortModel(safeSortModel);
        setPage(0);
        dispatch(
            getEntityAuditLogs(
                itemType,
                entityId,
                { currentPage: 1, recordsPerPage: PAGE_SIZE },
                toBackendSort(safeSortModel),
                null
            )
        );
    };

    const columns: GridColDef[] = [
        {
            field: 'userName',
            headerName: 'User Name',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <Typography variant="body2">{params.row.userName || '-'}</Typography>
            ),
        },
        {
            field: 'eventType',
            headerName: 'Event Type',
            flex: 0.7,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => (
                <Typography variant="body2">{params.row.eventType || '-'}</Typography>
            ),
        },
        {
            field: 'time',
            headerName: 'Date',
            flex: 0.8,
            minWidth: 150,
            sortable: true,
            valueGetter: (value) => parseDate(value, 'date-time'),
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.time ? convertDateToLocal(params.row.time) : '-'}
                </Typography>
            ),
        },
        {
            field: 'eventDetails',
            headerName: 'Event Details',
            flex: 1.5,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        py: 0.5,
                    }}
                >
                    {params.row.eventDetails || '-'}
                </Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.5,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => {
                const statusValue = params.row.status ?? 'Pending';
                const statusLower = String(statusValue).toLowerCase();

                const color: 'secondary' | 'error' | 'primary' =
                    ['completed', 'success', 'read', 'processed'].includes(statusLower)
                        ? 'secondary'
                        : ['failed', 'unread'].includes(statusLower)
                        ? 'error'
                        : 'primary';

                return (
                    <Chip
                        sx={{
                            borderRadius: '5px',
                            width: '90px',
                            height: '18px',
                            fontWeight: '400',
                            paddingTop: '1px',
                        }}
                        label={statusValue}
                        color={color}
                    />
                );
            },
        },
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Audit Logs</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Refresh">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleRefresh}
                                    disabled={pending}
                                >
                                    <CachedIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Loadable loading={pending}>
                    <Box sx={{ width: '100%', display: "flex", flexDirection: 'column'}}>
                        <DataGridPro
                            rows={list}
                            columns={columns}
                            getRowId={(row) => row.id}
                            getRowHeight={() => 'auto'}
                            disableRowSelectionOnClick
                            pagination
                            paginationMode="server"
                            sortingMode="server"
                            sortingOrder={['desc', 'asc']}
                            paginationModel={{ page, pageSize: PAGE_SIZE }}
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            onPaginationModelChange={handlePageChange}
                            pageSizeOptions={[PAGE_SIZE]}
                            rowCount={totalCount}
                            localeText={{ noRowsLabel: 'No audit logs found.' }}
                            sx={{
                                ...dataGridStyles,
                                '& .MuiDataGrid-cell': {
                                    alignItems: 'center',
                                    display: 'flex',
                                },
                                '& .MuiDataGrid-cellContent': {
                                    whiteSpace: 'normal',
                                    lineHeight: 1.4,
                                },
                                '& .MuiDataGrid-row': {
                                    alignItems: 'stretch',
                                },
                                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-cell:focus::after, & .MuiDataGrid-cell:focus-within::after': {
                                    display: 'none',
                                },
                            }}
                        />
                    </Box>
                </Loadable>
            </DialogContent>
        </Dialog>
    );
}

export default EntityAuditLogsDialog;